/**
 * Reusable dismissal hook combining Escape key + outside-click detection.
 *
 * Any transient UI layer (floating menus, popovers, drawers) wants the
 * same two guarantees:
 *   - press Escape while the layer is visible → dismiss.
 *   - click/tap anywhere outside the layer's DOM subtree → dismiss.
 *
 * This hook wires both event listeners on the document only while
 * `isActive` is `true`, and cleans them up as soon as the consumer
 * deactivates it. A list of CSS selectors can be provided to whitelist
 * specific ancestors (for example portaled MiniModals) that must not
 * trigger a dismissal.
 */

import { useEffect, type RefObject } from "react";

/** Options controlling the dismissal behaviour. */
export interface UseDismissOnOutsideInteractionOptions {

    /**
     * Master switch. Listeners are attached while `true` and cleaned up
     * synchronously when this flips to `false`.
     */
    "isActive": boolean;

    /**
     * Reference to the root element of the dismissable UI. Any pointer
     * event whose target is contained in this element is ignored.
     */
    "containerRef": RefObject<HTMLElement | null>;

    /**
     * Called when the Escape key is pressed or a pointer-down happens
     * outside of `containerRef` (and outside of any `ignoreSelectors`
     * ancestor).
     */
    "onDismiss": () => void;

    /**
     * Optional CSS selectors whose matches should NOT trigger a dismiss.
     * Useful when the UI is allowed to coexist with layered popups that
     * live in portals outside of `containerRef`, e.g.
     * `[data-mini-modal="true"]`.
     */
    "ignoreSelectors"?: readonly string[];

    /** When `true`, the Escape key listener is not attached. */
    "disableEscape"?: boolean;

    /** When `true`, the outside-click listener is not attached. */
    "disableOutsideClick"?: boolean;
}

/**
 * Attach Escape + outside-click dismissal while `isActive` is `true`.
 *
 * @param options Configuration. See `UseDismissOnOutsideInteractionOptions`.
 */
export const useDismissOnOutsideInteraction = ( options: UseDismissOnOutsideInteractionOptions ): void => {
    const {
        isActive,
        containerRef,
        onDismiss,
        ignoreSelectors,
        disableEscape = false,
        disableOutsideClick = false,
    } = options;

    useEffect( () => {
        if ( !isActive ) {
            return;
        }

        /**
         * Ignore pointer events whose target lives inside `containerRef`
         * or inside one of the whitelisted `ignoreSelectors` ancestors.
         * Everything else dismisses the UI.
         *
         * @param event The captured pointer-down event.
         */
        const handlePointerDown = ( event: PointerEvent ): void => {
            const target = event.target;
            if ( !( target instanceof Node ) ) {
                return;
            }
            const containerElement = containerRef.current;
            if ( containerElement !== null && containerElement.contains( target ) ) {
                return;
            }
            if ( ignoreSelectors !== undefined && ignoreSelectors.length > 0 && target instanceof Element ) {
                for ( const selector of ignoreSelectors ) {
                    if ( target.closest( selector ) !== null ) {
                        return;
                    }
                }
            }

            onDismiss();
        };

        /**
         * Dismiss the UI when the user presses Escape while it is active.
         *
         * @param event The captured key-down event.
         */
        const handleKeyDown = ( event: KeyboardEvent ): void => {
            if ( event.key === "Escape" ) {
                onDismiss();
            }
        };

        if ( !disableOutsideClick ) {
            document.addEventListener( "pointerdown", handlePointerDown );
        }
        if ( !disableEscape ) {
            document.addEventListener( "keydown", handleKeyDown );
        }

        return () => {
            if ( !disableOutsideClick ) {
                document.removeEventListener( "pointerdown", handlePointerDown );
            }
            if ( !disableEscape ) {
                document.removeEventListener( "keydown", handleKeyDown );
            }
        };
    }, [ containerRef, disableEscape, disableOutsideClick, ignoreSelectors, isActive, onDismiss ] );
};
